import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserCheck, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LogoHorizontal from '../components/LogoHorizontal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const InviteRegisterPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [validating, setValidating] = useState(true);
  const [invData, setInvData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/invitations/validate/${token}`);
        setInvData(res.data);
      } catch {
        setError('Invitación no válida o ya usada');
      } finally { setValidating(false); }
    };
    validate();
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Las contraseñas no coinciden'); return; }
    if (form.password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setRegistering(true);
    try {
      const res = await axios.post(`${API}/auth/register-invite`, {
        token, name: form.name, email: form.email, password: form.password,
      }, { withCredentials: true });
      setUser(res.data);
      toast.success(`Registrado como ${res.data.role}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar');
    } finally { setRegistering(false); }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="card-base p-8 max-w-md w-full text-center">
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="btn-primary">Ir al login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-primary)' }}>
      <div className="card-base p-8 max-w-md w-full" data-testid="invite-register">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <LogoHorizontal height={56} />
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>Has sido invitado a unirte como:</p>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${invData?.role === 'gestor' ? 'badge-blue' : 'badge-warning'}`}>
            {invData?.role === 'gestor' ? <><UserCheck className="w-4 h-4" /> Gestor</> : <><Eye className="w-4 h-4" /> Inversor</>}
          </div>
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Nombre completo</label>
            <input className="input-base" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="invite-name" />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Email</label>
            <input type="email" className="input-base" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="invite-email" />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Contraseña</label>
            <input type="password" className="input-base" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required data-testid="invite-password" />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Confirmar contraseña</label>
            <input type="password" className="input-base" value={form.confirmPassword} onChange={e => setForm({...form, confirmPassword: e.target.value})} required data-testid="invite-confirm" />
          </div>
          <button type="submit" disabled={registering} className="btn-primary w-full h-11" data-testid="invite-submit">
            {registering ? 'Registrando...' : 'Registrarme'}
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-4">
          ¿Ya tienes cuenta? <button onClick={() => navigate('/login')} className="text-blue-600 hover:underline">Iniciar sesión</button>
        </p>
      </div>
    </div>
  );
};

export default InviteRegisterPage;
